<template>
    <WaitingModal v-if="isXlsLoading || isPdfLoading"/>
    <div class="global-filter-container" v-if="!displayModalChoixSite">
        <div class="filter-container">
            <div class="filter-site-container" v-if="!isUniqueSite">
                {{$t('intervention.non-preventive.filter')}}
                <ButtonBlue
                    :on-click="selectionnerSite"
                    :text="$t('bouton.choix.site')"
                    class="button-confirm btn"
                    :disabled="tableData.length === 0 && isLoading"
                />
                <ReglePastille cle="facturation.suiviFinancierChoixSite" />
            </div>
            <Filter
                @search="search"
                @clearFilter="clearFilter"
                @setCreatedDateFrom="this.setCreatedDateFrom"
                @setCreatedDateTo="this.setCreatedDateTo"
                :filter="filter"
            />
        </div>

        <AccordionList class="container-accordion">
            <AccordionItem class="cursor-pointer" @click="displayOrHideSearch" @keydown.enter="displayOrHideSearch" @keydown.space.prevent="displayOrHideSearch" tabindex="0">
                <template #summary >{{ $t('facture.suivi.criteresAvances') }}</template>
                <Filter
                    @click.stop
                    @search="search"
                    @clearFilter="clearFilter"
                    @setInterventionId="setFilterInterventionId"
                    @comparison="setComparison"
                    @comparisonValue="setComparisonValue"
                    @setType="setType"
                    :filter="advancedFilter"
                    :status-list="statusList"
                    :comparison-list="comparisonList"
                    :type-intervention-list="typeInterventionListToString"
                    :hide-header="true"
                    :hide-button="true"
                    class="container-filter"
                />
            </AccordionItem>
        </AccordionList>
    </div>
    <ModalChoixSite v-else
                    @cancel="modalChoixSiteCancel"
                    @valider="validerSite"
                    :selected-site="null"
                    :selected-sites="filter.siteList"
                    :is-multi-site="true"/>

    <div class="list-container" v-if="!displayModalChoixSite">
        <BorderedIconButton
            v-if="!isLoading && tableData != null && tableData.length !== 0"
            class="export-button"
            @click="exportXlsSuiviFinancier"
            :imgUrl="iconeXls" :title="$t('list.export')"/>
        <ReglePastille v-if="!isLoading && tableData != null && tableData.length !== 0" cle="facturation.suiviFinancierExport" />
        <LoaderComponent v-if="tableData != null && tableData.length === 0 && isLoading" class="loader" />
        <div v-else @scroll="handleScroll" ref="content" class="datatable" :class="{'datatable-smaller': accordionOpen}">
            <DataTable
                :tableHeader="tableHeader"
                :tableData="tableData"
                :isLoading="isLoading"
                :noDataText="$t('facture.suivi.noData')"
                :stop-propa-on-pdf='true'
                @downloadPdf="downloadPdf"
                @sort="doSort"
                @select="downloadPdf"
            />
        </div>
    </div>

</template>

<script lang="ts">
import DataTable from '@/components/ui-components/DataTable.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import { SortType, sortValue } from '@/models/SortType';
import { defineComponent, onMounted, ref } from 'vue';
import { AccordionItem, AccordionList } from 'vue3-rich-accordion';
import Filter from '@/modules/Common/components/Filter.vue';
import SiteListService, { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import { EtatsTravaux } from '@/models/InterventionNonPreventive';
import SuiviFacturesService from '../services/SuiviFacturesService';
import { SuiviFinancier } from '@/modules/SuiviFactures/store/modules/SuiviFinancier/action-types';
import { Site } from '@/models/Site';
import dayjs from 'dayjs';
import { SUIVI_FINANCIER } from '@/modules/SuiviFactures/store/modules/SuiviFinancier/mutation-types';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import ModalChoixSite from '@/modules/Common/components/ModalChoixSite.vue';
import { store } from '@/plugins/store';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface TypeIntervention {
    id: number;
    libelle: string;
}

interface Data {
    typeInterventionList: TypeIntervention[];
    typeInterventionListToString: string[];
    siteList: any[];
    statusList: string[];
    comparisonList: string[];
    filter: {
        siteList: any[];
        from: string;
        to: string;
    };
    advancedFilter: {
        typeIntervention: TypeIntervention;
        interventionId: string;
        comparison: string | null;
        comparisonValue: number | null;
    };
    debounce: number;
    accordionOpen: boolean;
    displayModalChoixSite: boolean;
    isUniqueSite: boolean;
}

export default defineComponent({
    name: 'SuiviFinancier',
    beforeMount() {
        const sortData = 'dateFinReellle';
        const sortStatus = SortType.DESC;
        this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.SET_SORT_SUIVI_FINANCIER}`, { sortData, sortStatus });
        this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.RESET_ITEMS}`);
        this.filter.siteList = this.filterSite;
        // On n'affiche pas la modale de choix de site lorsqu'on arrive sur la page
        this.displayModalChoixSite = false;

        // On r�cup�re la liste des sites
        SiteListService.getSiteList(new SiteSearchDTO(0, '', true)).then(res => {
            if (res.data.length === 1) {
                this.isUniqueSite = true;
                this.displayModalChoixSite = false;
            }
            this.siteList = res.data.map(
                ({ id, chantierId, description }: { id: string; chantierId: number; description: string }) => ({
                    id,
                    chantierId,
                    description,
                })
            );

            this.statusList = Object.values(EtatsTravaux);
            this.filter.siteList = this.siteList;

            if (!this.displayModalChoixSite) {
                this.getFacturesData();
            }
        });
    },
    mounted() {
        this.content?.scrollTo({ top: this.scroll });
        SuiviFacturesService.getTypesIntervention().then((res: any) => {
            this.typeInterventionList = res.data.map(
                ({id, libelle}: { id: string; libelle: string }) => ({
                  id, libelle
                })
            );
            this.typeInterventionList.unshift({id: 0, libelle:'Tous'});
            this.typeInterventionList.forEach(type => {
                this.typeInterventionListToString.push(type.libelle);
            });
        });
    },
    components: {
        AccordionList,
        DataTable,
        LoaderComponent,
        AccordionItem,
        Filter,
        BorderedIconButton,
        WaitingModal,
        ButtonBlue,
        ModalChoixSite,
        ReglePastille
    },
    data(): Data  {
        return {
            typeInterventionList: [],
            typeInterventionListToString: [],
            siteList: [],
            statusList: [],
            comparisonList: [
                this.$t('facture.suivi.dropdown.novalue'),
                this.$t('facture.suivi.dropdown.lessthan'),
                this.$t('facture.suivi.dropdown.greaterthan'),
                this.$t('facture.suivi.dropdown.equal')
            ],
            filter: {
                siteList: [],
                from: '',
                to: dayjs().format('YYYY-MM-DD'),
            },
            advancedFilter:{
                typeIntervention: { id:0, libelle: '' },
                interventionId: '',
                comparison: this.$t('facture.suivi.dropdown.novalue'),
                comparisonValue: null
            },
            debounce: 0,
            accordionOpen: false,
            displayModalChoixSite: true,
            isUniqueSite: false,
        };
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    computed: {
        tableData() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFinancesItems'];
        },
        page() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFinancesPage'];
        },
        sort() : {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFinancesSort'];
        },
        isLoading() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFinancesIsLoading'];
        },
        isXlsLoading() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFinancesXlsIsLoading'];
        },
        isPdfLoading() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFinancesPdfIsLoading'];
        },
        numberOfPages() {
             return this.$store.getters['suiviFacturesModuleState/getSuiviFinancesNbOfPages'];
        },
        scroll() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFinancesScroll'];
        },
        tableHeader() {
            return this.$store.getters['suiviFacturesModuleState/getSuiviFinancesTableHeader'];
        },
        filterSite(): any[] {
            return this.$store.getters['suiviDemandeInterventionModuleState/getDemandeInterventionFilterSite'];
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
    },
    methods: {
        downloadPdf(item: any) {
            const facture = this.$store.getters['suiviFacturesModuleState/getSuiviFinancesFullItems'].find((f: any) => f.reference === item.reference);
            if(item.facturee) {
                this.$store.dispatch(`suiviFacturesModuleState/${SuiviFinancier.GET_FACTURE_PDF}`,
                    {interventionId: item.reference, chantierId: facture.chantierId});
            }
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.SET_SCROLL}`, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.getFacturesData(this.page + 1);
            }
        },
        getFacturesData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });
            const typeInterventionExtranetId: number[] = this.advancedFilter?.typeIntervention?.id === 0 ? [] : [this.advancedFilter?.typeIntervention?.id];
            this.$store.dispatch(`suiviFacturesModuleState/${SuiviFinancier.GET_SUIVI_FINANCIER}`, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                chantierIds: this.siteList,
                dateDebutPeriodeRechercheParDate: this.filter.from,
                dateFinPeriodeRechercheParDate: this.filter.to,
                comparateur: this.advancedFilter.comparison,
                montant: this.advancedFilter.comparisonValue,
                id: this.advancedFilter.interventionId,
                typeInterventionExtranetId: typeInterventionExtranetId
            });
        },
        displayOrHideSearch() : void {
            this.accordionOpen = !this.accordionOpen;
        },
        setComparison(comparison: string) {
            this.advancedFilter.comparison = comparison;
        },
        setComparisonValue(comparisonValue: number) {
            this.advancedFilter.comparisonValue = comparisonValue;
        },
        setCreatedDateFrom(value: string): void {
            this.filter.from = value;
        },
        setCreatedDateTo(value: string): void {
            const to : Date = new Date(value);
            const from : Date = new Date(this.filter.from);
            if(from > to){
                this.filter.from = '';
            }
            this.filter.to = value;
        },
        setFilterInterventionId(value: string) {
            this.advancedFilter.interventionId = value;
        },
        setType(value: string): void {
            this.advancedFilter.typeIntervention = this.typeInterventionList.find(type => type.libelle === value) ?? this.typeInterventionList[0];
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.SET_SORT_SUIVI_FINANCIER}`, { sortData, sortStatus });
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.RESET_ITEMS}`);
            this.getFacturesData();
        },
        search() {
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.RESET_ITEMS}`);
            this.doFilter();
        },
        doFilter() {
            const chantierIds: number[] = this.filter.siteList.map(site => site.chantierId);
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.SET_FILTER_SITE}`, chantierIds);
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.SET_FILTER_DATE_FROM}`, this.filter.from);
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.SET_FILTER_DATE_TO}`, this.filter.to);
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.SET_FILTER_TYPE_INTERVENTION_ID}`, this.advancedFilter.typeIntervention?.id);
            this.getFacturesData();
        },
        clearFilter() {
            this.$store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.RESET_ITEMS}`);
            this.filter =  {
                siteList: [],
                from: '',
                to: ''
            };
            this.advancedFilter = {
                typeIntervention: { id:0, libelle: '' },
                interventionId: '',
                comparison: this.$t('facture.suivi.dropdown.novalue'),
                comparisonValue: null
            };
            this.doFilter();
        },
        exportXlsSuiviFinancier() {
            this.$store.dispatch(`suiviFacturesModuleState/${SuiviFinancier.GET_SUIVI_FINANCIER_XLS}`);
        },
        validerSite(sites: Site[]) : void {
            this.siteList = sites;
            this.displayModalChoixSite = false;
            const chantierIds: number[] = sites.map(site => site.chantierId);
            store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.SET_FILTER_SITE}`, chantierIds);
            store.commit(`suiviFacturesModuleState/${SUIVI_FINANCIER.RESET_ITEMS}`);
            this.getFacturesData();
        },
        modalChoixSiteCancel(): void {
            this.displayModalChoixSite = false;
            if (this.tableData.length === 0 && !this.isLoading) this.getFacturesData();
        },
        selectionnerSite() : void {
            this.displayModalChoixSite = true;
        }
    },
});
</script>

<style lang="scss" scoped>
.filter-container {
    background-color: var(--white);
    padding: 13px;
    border-radius: 6px;
    box-shadow: none;
}
.list-container {
    background-color: var(--white);
    padding: 16px;
    border-radius: 6px;
    margin-top: 12px;
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, .2), 0 2px 2px rgba(0, 0, 0, .14), 0 1px 5px rgba(0, 0, 0, .12);
}
.title {
    color: var(--color-principal);
    margin-bottom: 16px;
    font-size: var(--font-size-medium);
    font-weight: 500;
}

@import '@/theme/pages-recherche.scss';

.datatable {
    max-height: calc(100vh - 480px);
    overflow-y: auto;
}
.datatable-smaller {
    max-height: calc(100vh - 550px);
}
.container-accordion {
    cursor: initial;
}
.cursor-pointer {
    cursor: pointer;
}
.container-filter {
    padding-top: 20px;
}
:deep(.accordion-item__summary){
    color: var(--color-principal);
}
:deep(.pointer) {
    cursor: default;
}
.export-button {
    margin-bottom: 5px;
    //width: 95px;
    margin-right: 0;
    height: 24px;
    margin-left: auto;
}
.global-filter-container {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 6px;
    margin-bottom: 8px;
    padding: 8px 16px;
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, .2), 0 2px 2px rgba(0, 0, 0, .14), 0 1px 5px rgba(0, 0, 0, .12);
}
.filter-container {
    display: flex;
}
.filter-site-container{
    flex-direction: column;
    width: 15%;
}
.filter {
    width: 99%;
}
.button-confirm {
    right: 16px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 20px;
}
:deep(.filter) {
    margin-bottom: 0;
    padding: 0 16px;
}
</style>
